import { IconAlarm, IconCalendarCheck, IconHandStop, IconMail, IconPlus, IconRoute, IconSearch, IconUser } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import './bookingI18n';
import { Button, Segmented, Skeleton, StateView, type ScreenState } from '@hana/ui';
import { PageHeader } from '@hana/shell';
import { BOOKING_COVER, rowCutoff, rqStatus, RL_TABS, shipStatus, type BookingGroup, type BookingScope, type RlTab } from './booking';
import { BOOKING_P0, BOOKING_VESSELS } from './fixtures';
import type { BookingRequest } from './types';
import s from './bookingPad.module.css';

// Mint-Booking-iPad (1180): thanh phạm vi + điều phối + info@ trên đầu trang, dải P0, dải tab cuộn ngang, ô tìm,
// nhóm theo tàu (tên tàu + hãng · ETD), dòng RQ hai phần (mã · cont · trạng thái / khách / hãng · booking · chủ job / SI · CY). Chỉ đọc.

const DOT = { amber: 'var(--d-amber)', blue: 'var(--d-blue)', gray: 'var(--d-gray)' } as const;
const SCOPES: ReadonlyArray<{ key: BookingScope; label: string }> = [
  { key: 'mine', label: 'Của tôi' },
  { key: 'cover', label: 'Tôi thay' },
  { key: 'un', label: 'Chưa nhận' },
  { key: 'team', label: 'Cả team' },
];

export interface BookingPadProps {
  me: string;
  state: ScreenState;
  scope: BookingScope;
  onScope: (k: BookingScope) => void;
  ownCounts: Record<BookingScope, number>;
  tab: RlTab;
  counts: Record<RlTab, number>;
  lock: boolean;
  onTab: (k: RlTab) => void;
  q: string;
  onQ: (v: string) => void;
  groups: BookingGroup[];
  ack: Record<string, true>;
  onAck: (code: string) => void;
  inboxN: number;
  onOpen: (r: BookingRequest) => void;
  onVessel: (name: string) => void;
  onWrite: () => void;
  onRetry: () => void;
}

const sub = (r: BookingRequest) => `SI ${r.siCutoff ?? '—'} · CY ${r.cyCutoff ?? '—'}`;

function PadRow({ r, onOpen }: { r: BookingRequest; onOpen: (r: BookingRequest) => void }) {
  const t = useT();
  const ship = shipStatus(r);
  const cut = rowCutoff(r);
  return (
    <button type="button" className={s.row} onClick={() => onOpen(r)}>
      <span className={s.l1}>
        <b className={s.job}>{r.jobId}</b>
        <span className={s.cont}>{r.cont}</span>
        <span className={s.st} title="ShipmentStatus">
          <span className={s.dot} style={{ background: DOT[ship.dot] }} />
          {rqStatus(r)}
        </span>
      </span>
      <span className={s.cus}>{r.customer}</span>
      <span className={s.l3}>
        <span>{r.carrier}</span>
        <span title={t('booking.a11y20')} className={r.bookingNo ? undefined : s.warnTx}>
          <IconCalendarCheck size={13} aria-hidden="true" />
          {r.bookingNo ? `BK ${r.bookingNo}` : 'chưa có booking'}
        </span>
        <span title={t('booking.a11y21')}>
          <IconUser size={13} aria-hidden="true" />
          {r.owner ?? 'Chưa nhận'}
        </span>
      </span>
      <span className={s.l3}>
        <span className={cut?.cd.tone === 'err' ? s.errTx : undefined}>{sub(r)}</span>
        {cut && (
          <b className={`${s.cd} ${s[`cd_${cut.cd.tone}`]}`} title={cut.tip}>
            <IconAlarm size={13} aria-hidden="true" />
            {cut.cd.k}
          </b>
        )}
      </span>
    </button>
  );
}

export function BookingPad(p: BookingPadProps) {
  const t = useT();
  const ready = p.state === 'ready';
  const p0 = BOOKING_P0[0];
  const done = p0 ? !!p.ack[p0.code] : true;
  const who = p0 ? (BOOKING_COVER[p0.pic] ? `${BOOKING_COVER[p0.pic]} (thay ${p0.pic})` : p0.pic) : '';
  return (
    <>
      <PageHeader
        title={t('booking.a11y1')}
        tabs={
          <Segmented kind="toggle" label="Phạm vi" value={p.scope} onChange={p.onScope} items={SCOPES.map((x) => ({ key: x.key, label: x.label, n: p.ownCounts[x.key] }))} />
        }
        actions={
          <>
            <div role="group" aria-label={t('booking.a11y22')} className={s.coord}>
              <Button variant="ghost" icon={<IconRoute size={16} aria-hidden="true" />} aria-label={`Lịch điều phối mail chung · ${p.me}`} onClick={p.onWrite}>
                {p.me}
              </Button>
              <button type="button" role="switch" aria-checked="false" aria-label={t('booking.a11y23')} className={s.sw} onClick={p.onWrite}>
                <span className={s.knob} />
              </button>
            </div>
            <Button variant="secondary" icon={<IconMail size={18} aria-hidden="true" />} aria-label={`Hộp thư info@ · ${p.inboxN} thư mới`} onClick={p.onWrite}>
              info@ <span className={s.badge}>{p.inboxN}</span>
            </Button>
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={p.onWrite}>
              {t('booking.newRq')}
            </Button>
          </>
        }
      />
      {ready && p0 && (
        <div role="alert" className={s.p0}>
          <span className={s.p0Code}>P0 · {p0.code}</span>
          <div className={s.p0Mid}>
            <b>
              {p0.k}{' '}
              <span className={s.p0Left} title={`Còn ${p0.left}`}>
                <IconAlarm size={13} aria-hidden="true" />
                {p0.left}
              </span>
            </b>{' '}
            <span className={s.sm}>{p0.ref}</span>
            <div className={s.sm}>{`Đã báo ${p0.minutes}′ · 0′ ${who} → 30′ Sale ${p0.sale} → 60′ Trưởng phòng Sales`}</div>
          </div>
          {done ? (
            <span className={s.ok}>{t('booking.claimed')}</span>
          ) : (
            <Button variant="primary" icon={<IconHandStop size={16} aria-hidden="true" />} onClick={() => p.onAck(p0.code)}>
              {t('booking.mine')}
            </Button>
          )}
        </div>
      )}
      <div role="tablist" aria-label={t('booking.a11y24')} className={s.tabs}>
        {[...RL_TABS.filter((t) => t.key === 'all'), ...RL_TABS.filter((t) => t.key !== 'all')].map((t) => {
          const dis = p.lock && t.key !== 'prio';
          const on = p.tab === t.key;
          return (
            <button key={t.key} type="button" role="tab" aria-selected={on} aria-disabled={dis} className={`${s.tab} ${on ? s.on : ''} ${dis ? s.dis : ''}`} onClick={() => p.onTab(t.key)}>
              {t.label}
              <span className={s.n}>{p.counts[t.key]}</span>
            </button>
          );
        })}
      </div>
      <section aria-label={t('booking.a11y25')} className={s.list}>
        <label className={s.search}>
          <IconSearch size={16} aria-hidden="true" />
          <input aria-label={t('booking.a11y26')} placeholder={t('booking.a11y27')} value={p.q} onChange={(e) => p.onQ(e.target.value)} />
        </label>
        <div className={s.scroll}>
          <StateView
            state={ready && p.groups.every((g) => g.rows.length === 0) ? 'empty' : p.state}
            title={ready ? 'Không có yêu cầu khớp' : p.state === 'empty' ? 'Hàng chờ RQ đang trống' : undefined}
            action={p.state === 'error' ? <Button onClick={p.onRetry}>{t('booking.retry')}</Button> : undefined}
            skeleton={<Skeleton rows={6} />}
          >
            {p.groups.map((g) => {
              const v = g.vessel ? BOOKING_VESSELS[g.vessel] : undefined;
              return (
                <div key={g.vessel || 'flat'}>
                  {v && (
                    <div className={s.gh}>
                      <div className={s.ghRow}>
                        {g.vessel !== 'none' ? (
                          <button type="button" className={s.vsl} data-vlink={v.name.replace(/\s(\S+)$/, '|$1')} title={t('booking.a11y28')} onClick={() => p.onVessel(v.name)}>
                            {v.name}
                          </button>
                        ) : (
                          <span className={s.vsl}>{v.name}</span>
                        )}
                        <span className={s.sm}>{g.rows.length} RQ</span>
                      </div>
                      <div className={s.sm}>{v.line}</div>
                      {v.change && (
                        <div className={s.chg}>
                          <b className={s.chgK}>{t('booking.reschedule')}</b> ETD <s>{v.change.from}</s> → <b>{v.change.to}</b> · {v.change.why}
                        </div>
                      )}
                    </div>
                  )}
                  {g.rows.map((r) => (
                    <PadRow key={r.id} r={r} onOpen={p.onOpen} />
                  ))}
                </div>
              );
            })}
          </StateView>
        </div>
      </section>
    </>
  );
}

